import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, take } from 'rxjs';

type ElevenLabsConvaiWidgetElement = HTMLElement & {
  startConversation?: () => unknown | Promise<unknown>;
  endConversation?: () => unknown | Promise<unknown>;
};

@Injectable({
  providedIn: 'root',
})
export class ElevenLabsWidgetService {
  private widgetEl: ElevenLabsConvaiWidgetElement | null = null;
  private conversationEndedListener: (() => void) | null = null;

  private visibleSubject = new BehaviorSubject<boolean>(false);
  readonly visible$ = this.visibleSubject.asObservable();

  constructor(private zone: NgZone) {}

  get isVisible(): boolean {
    return this.visibleSubject.value;
  }

  registerWidgetElement(el: HTMLElement | null): void {
    if (this.widgetEl === el) {
      return;
    }

    this.detachListeners();
    this.widgetEl = el as ElevenLabsConvaiWidgetElement | null;
    this.attachListeners();
  }

  open(): void {
    if (!this.widgetEl) {
      return;
    }

    this.visibleSubject.next(true);

    this.zone.runOutsideAngular(() => {
      // Wait until Angular finishes rendering/unhiding the element.
      this.zone.onStable.pipe(take(1)).subscribe(() => {
        const start = () => {
          try {
            this.widgetEl?.startConversation?.();
          } catch {}
        };

        const customElementsApi = (globalThis as any)?.customElements;
        if (customElementsApi?.whenDefined) {
          customElementsApi.whenDefined('elevenlabs-convai').then(start, start);
        } else {
          start();
        }
      });
    });
  }

  close(): void {
    if (!this.widgetEl) {
      return;
    }

    this.visibleSubject.next(false);

    this.zone.runOutsideAngular(() => {
      try {
        this.widgetEl?.endConversation?.();
      } catch {
        // ignore
      }
    });
  }

  toggle(): void {
    if (this.isVisible) {
      this.close();
      return;
    }

    this.open();
  }

  private attachListeners(): void {
    if (!this.widgetEl) {
      return;
    }

    this.conversationEndedListener = () => {
      this.zone.run(() => this.visibleSubject.next(false));
    };

    this.widgetEl.addEventListener('conversationEnded', this.conversationEndedListener);
  }

  private detachListeners(): void {
    if (this.widgetEl && this.conversationEndedListener) {
      this.widgetEl.removeEventListener('conversationEnded', this.conversationEndedListener);
    }
    this.conversationEndedListener = null;
  }
}
